import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import { Map as MapIcon, ChevronUp, LogIn } from 'lucide-react';
import PortalBottomNav from '../components/PortalBottomNav';
import PublicChatWidget from '../components/PublicChatWidget';
import { toast } from 'sonner';

const STATUSES = [
  { id: 'planned', label: 'Planned', dot: 'bg-amber-500', bg: 'bg-amber-50' },
  { id: 'in_progress', label: 'In Progress', dot: 'bg-blue-500', bg: 'bg-blue-50' },
  { id: 'shipped', label: 'Shipped', dot: 'bg-emerald-500', bg: 'bg-emerald-50' },
];

const PublicRoadmap = () => {
  const { subdomain } = useParams();
  const [searchParams] = useSearchParams();
  const accessKey = searchParams.get('key') || '';
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [items, setItems] = useState([]);
  const [myVotes, setMyVotes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => { load(); }, [subdomain]);

  const load = async () => {
    setLoading(true);
    try {
      const keyParam = accessKey ? `?key=${accessKey}` : '';
      const [ws, items] = await Promise.all([
        axios.get(`${API_URL}/public/workspaces/${subdomain}${keyParam}`),
        axios.get(`${API_URL}/public/workspaces/${subdomain}/roadmap`),
      ]);
      setWorkspace(ws.data);
      setItems(items.data.items || []);
      if (token) {
        try {
          const v = await axios.get(`${API_URL}/public/workspaces/${subdomain}/roadmap/my-votes`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setMyVotes(new Set(v.data.item_ids || []));
        } catch (_) { void _; }
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async (item) => {
    if (!token) {
      toast.error('Please log in to upvote');
      navigate(`/auth?next=/portal/${subdomain}/roadmap${accessKey ? `?key=${accessKey}` : ''}`);
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/public/workspaces/${subdomain}/roadmap/${item.id}/upvote`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, upvote_count: res.data.upvote_count } : i));
      setMyVotes(prev => {
        const next = new Set(prev);
        if (res.data.upvoted) next.add(item.id); else next.delete(item.id);
        return next;
      });
    } catch (e) {
      toast.error('Vote failed');
    }
  };

  const primaryColor = workspace?.settings?.primary_color || '#7c3aed';

  return (
    <div className="min-h-screen bg-zinc-50 pb-24" data-testid="public-roadmap-page">
      <div style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}>
        <div className="max-w-5xl mx-auto px-6 py-12 text-white">
          <div className="flex items-center gap-3 mb-2">
            <MapIcon className="h-6 w-6 opacity-80" />
            <span className="text-sm font-medium opacity-90">{workspace?.name}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
            Roadmap
          </h1>
          <p className="mt-2 text-white/80">See what we're building. Vote on what matters most to you.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {!token && (
          <div className="mb-4 p-3 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-violet-900">
              <LogIn className="h-4 w-4" />
              <span>Sign in to upvote feature requests</span>
            </div>
            <button onClick={() => navigate('/auth')} className="text-violet-700 font-semibold hover:underline" data-testid="roadmap-login-cta">Sign in →</button>
          </div>
        )}

        {loading ? <p className="text-center py-12 text-zinc-500">Loading…</p> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {STATUSES.map(col => {
              const colItems = items.filter(i => i.status === col.id);
              return (
                <div key={col.id} className={`${col.bg} rounded-2xl p-4`}>
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                    <span className="font-bold text-sm text-zinc-800">{col.label}</span>
                    <span className="text-xs text-zinc-500 ml-auto">{colItems.length}</span>
                  </div>
                  <div className="space-y-2">
                    {colItems.length === 0 ? (
                      <div className="text-xs text-zinc-400 italic px-2 py-4 text-center">Nothing here</div>
                    ) : colItems.map(item => (
                      <div key={item.id} className="bg-white border border-zinc-200 rounded-xl p-3" data-testid={`public-roadmap-item-${item.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-sm text-zinc-900 leading-snug flex-1">{item.title}</h3>
                          <button
                            onClick={() => handleUpvote(item)}
                            className={`flex flex-col items-center px-2 py-1.5 rounded-lg border transition-all ${
                              myVotes.has(item.id) ? 'text-white border-transparent' : 'border-zinc-200 text-zinc-700 hover:border-zinc-300'
                            }`}
                            style={myVotes.has(item.id) ? { backgroundColor: primaryColor } : {}}
                            data-testid={`upvote-${item.id}`}
                          >
                            <ChevronUp className="h-3 w-3" />
                            <span className="text-xs font-bold">{item.upvote_count || 0}</span>
                          </button>
                        </div>
                        {item.category && <div className="text-xs text-zinc-500 mt-2">{item.category}</div>}
                        {item.description && (
                          <div className="text-xs text-zinc-600 mt-2 line-clamp-3 prose prose-xs" dangerouslySetInnerHTML={{ __html: item.description }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PortalBottomNav workspace={workspace} accessKey={accessKey} />
      {workspace?.settings?.show_ai_chat !== false && (
        <>
          {!chatOpen && (
            <button
              onClick={() => setChatOpen(true)}
              className="fixed bottom-24 right-6 z-50 h-14 px-5 rounded-full shadow-xl text-white font-semibold hover:scale-105 transition-transform"
              style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}
            >
              {workspace?.settings?.ai_button_text || 'Ask AI'}
            </button>
          )}
          {chatOpen && (
            <PublicChatWidget subdomain={subdomain} workspaceName={workspace?.name} primaryColor={primaryColor} onClose={() => setChatOpen(false)} settings={workspace?.settings} accessKey={accessKey} />
          )}
        </>
      )}
    </div>
  );
};

export default PublicRoadmap;
